/**
 * Community API Layer
 * Queries for OTHER users' data go through the backend (service role key).
 * Queries for the CURRENT user's own data stay as direct Supabase (anon key + RLS).
 *
 * This prevents users from modifying frontend queries (via DevTools) to select
 * sensitive columns (email, preferences, tier) from other users' profiles.
 */

import { getSession } from './supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// ─── Simple in-memory cache (avoids re-fetching on tab switches) ─────────────
const _cache = new Map();
const DEFAULT_TTL = 60_000; // 1 minute

function getCached(key) {
  const entry = _cache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiry) { _cache.delete(key); return null; }
  return entry.data;
}

function setCache(key, data, ttl = DEFAULT_TTL) {
  _cache.set(key, { data, expiry: Date.now() + ttl });
}

/** Invalidate cache entries matching a prefix (e.g. 'reviews' clears 'reviews:*') */
export function invalidateCache(prefix) {
  for (const key of _cache.keys()) {
    if (key.startsWith(prefix)) _cache.delete(key);
  }
}

/**
 * Helper — get auth headers for backend requests.
 * Returns { Authorization: 'Bearer <token>' } or empty object if not logged in.
 */
const getAuthHeaders = async () => {
  const session = await getSession();
  if (!session?.access_token) return {};
  return { Authorization: `Bearer ${session.access_token}` };
};

// ============================================================================
// PROFILE (routed through backend — accesses OTHER users' data)
// ============================================================================

/**
 * Get a wanderer's community profile (public fields only — no email, no preferences).
 * Routed through backend to enforce column-level security.
 */
export const getWandererProfile = async (userId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/profile/${userId}`, {
      headers: { ...authHeaders },
    });

    if (!res.ok) {
      console.error('Error fetching wanderer profile:', res.status);
      return null;
    }
    return await res.json();
  } catch (err) {
    console.error('Error in getWandererProfile:', err);
    return null;
  }
};

/**
 * Update community profile fields (routed through backend for server-side validation,
 * rate limiting, profanity checks, and uniqueness enforcement).
 */
export const updateWandererProfile = async (userId, updates) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(updates),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => null);
      console.error('Error updating wanderer profile:', res.status, errorData);
      return null;
    }
    return await res.json();
  } catch (err) {
    console.error('Error in updateWandererProfile:', err);
    return null;
  }
};

/**
 * Get profile stats (routed through backend — counts from related tables)
 */
export const getProfileStats = async (userId) => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/community/stats/${userId}`);

    if (!res.ok) {
      console.error('Error fetching profile stats:', res.status);
      return { followers_count: 0, following_count: 0, trips_logged: 0, reviews_written: 0 };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in getProfileStats:', err);
    return { followers_count: 0, following_count: 0, trips_logged: 0, reviews_written: 0 };
  }
};

// ============================================================================
// FOLLOW SYSTEM (routed through backend for block checks and validation)
// ============================================================================

/**
 * Follow a user (routed through backend for block-check and target validation)
 */
export const followUser = async (targetId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/follow/${targetId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
    });

    const data = await res.json().catch(() => null);
    if (!res.ok) {
      return { success: false, error: data?.error || 'Failed to follow user' };
    }
    return { success: true, data };
  } catch (err) {
    console.error('Error in followUser:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Unfollow a user (routed through backend)
 */
export const unfollowUser = async (targetId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/follow/${targetId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
    });

    const data = await res.json().catch(() => null);
    if (!res.ok) {
      return { success: false, error: data?.error || 'Failed to unfollow user' };
    }
    return { success: true };
  } catch (err) {
    console.error('Error in unfollowUser:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Check if current user is following a target user (own data — direct Supabase)
 */
export const isFollowing = async (targetId) => {
  try {
    const authHeaders = await getAuthHeaders();
    if (!authHeaders.Authorization) return false;

    const res = await fetch(`${BACKEND_URL}/api/community/is-following/${targetId}`, {
      headers: { ...authHeaders },
    });

    if (!res.ok) return false;
    const result = await res.json();
    return result.following === true;
  } catch (err) {
    console.error('Error in isFollowing:', err);
    return false;
  }
};

/**
 * Get followers list (routed through backend — accesses other users' profiles)
 */
export const getFollowers = async (userId, limit = 50, offset = 0) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(
      `${BACKEND_URL}/api/community/followers/${userId}?limit=${limit}&offset=${offset}`,
      { headers: { ...authHeaders } }
    );

    if (!res.ok) {
      console.error('Error fetching followers:', res.status);
      return { followers: [], total: 0 };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in getFollowers:', err);
    return { followers: [], total: 0 };
  }
};

/**
 * Get following list (routed through backend — accesses other users' profiles)
 */
export const getFollowing = async (userId, limit = 50, offset = 0) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(
      `${BACKEND_URL}/api/community/following/${userId}?limit=${limit}&offset=${offset}`,
      { headers: { ...authHeaders } }
    );

    if (!res.ok) {
      console.error('Error fetching following:', res.status);
      return { following: [], total: 0 };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in getFollowing:', err);
    return { following: [], total: 0 };
  }
};

// ============================================================================
// DISPLAY NAME & SEARCH (routed through backend — queries other users)
// ============================================================================

/**
 * Check if a display name is already taken (case-insensitive).
 * Routed through backend — requires auth (excludes current user from check).
 * Returns { taken: boolean }
 */
export const isDisplayNameTaken = async (displayName, currentUserId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(
      `${BACKEND_URL}/api/community/check-display-name?name=${encodeURIComponent(displayName)}`,
      { headers: { ...authHeaders } }
    );

    if (!res.ok) {
      console.error('Error checking display name:', res.status);
      return { taken: false }; // fail open
    }

    const data = await res.json();
    return { taken: !data.available };
  } catch (err) {
    console.error('Error in isDisplayNameTaken:', err);
    return { taken: false }; // fail open
  }
};

// ============================================================================
// SHARE LINKS (routed through backend — needs service role for cross-user reads)
// ============================================================================

/**
 * Create a share link for an itinerary.
 * @param {string} itineraryId - The saved itinerary UUID
 * @param {object} options - { expiry: '24h'|'7d'|'30d'|'never', includeBudget: boolean }
 * @returns {{ share_hash, share_expires_at, include_budget_in_share } | null}
 */
export const createShareLink = async (itineraryId, { expiry = '7d', includeBudget = false } = {}) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/itinerary/${itineraryId}/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ expiry, includeBudget }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('Error creating share link:', res.status, err);
      return { error: err.error || 'Failed to create share link' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in createShareLink:', err);
    return { error: err.message };
  }
};

/**
 * Get the current share status of an itinerary.
 * @param {string} itineraryId
 * @returns {{ share_hash, share_expires_at, include_budget_in_share, visibility } | null}
 */
export const getShareStatus = async (itineraryId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/itinerary/${itineraryId}/share`, {
      headers: { ...authHeaders },
    });

    if (!res.ok) {
      console.error('Error fetching share status:', res.status);
      return null;
    }
    return await res.json();
  } catch (err) {
    console.error('Error in getShareStatus:', err);
    return null;
  }
};

/**
 * Revoke a share link for an itinerary.
 * @param {string} itineraryId
 * @returns {{ success: boolean, error?: string }}
 */
export const revokeShareLink = async (itineraryId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/itinerary/${itineraryId}/share`, {
      method: 'DELETE',
      headers: { ...authHeaders },
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('Error revoking share link:', res.status, err);
      return { success: false, error: err.error || 'Failed to revoke share link' };
    }
    return { success: true };
  } catch (err) {
    console.error('Error in revokeShareLink:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Get a shared itinerary by hash.
 * Sends auth headers if user is logged in — backend returns full data for
 * authenticated users, preview-only for guests (beta sign-up gate).
 * @param {string} shareHash
 * @returns {{ title, destination, itinerary_data?, author_name, requires_auth, ... } | null}
 */
/**
 * View a community-published itinerary by ID.
 */
export const getCommunityItinerary = async (itineraryId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/itinerary/${itineraryId}/view`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error('Error in getCommunityItinerary:', err);
    return null;
  }
};

export const getSharedItinerary = async (shareHash) => {
  try {
    // Send auth headers if available — authenticated users get full itinerary
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/shared/${shareHash}`, {
      headers: { ...authHeaders },
    });

    if (res.status === 410) {
      return { expired: true, error: 'This share link has expired' };
    }
    if (!res.ok) {
      console.error('Error fetching shared itinerary:', res.status);
      return null;
    }
    return await res.json();
  } catch (err) {
    console.error('Error in getSharedItinerary:', err);
    return null;
  }
};

// ============================================================================
// PUBLISH TO COMMUNITY (routed through backend — requires auth)
// ============================================================================

/**
 * Publish an itinerary to the community (visible to signed-in community members).
 * @param {string} itineraryId - The saved itinerary UUID
 * @param {object} options - { includeBudget: boolean }
 * @returns {{ id, visibility, include_budget_in_share, published_at } | { error }}
 */
export const publishToCommunity = async (itineraryId, { includeBudget = false } = {}) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/itinerary/${itineraryId}/publish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ includeBudget }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('Error publishing to community:', res.status, err);
      return { error: err.error || 'Failed to publish to community' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in publishToCommunity:', err);
    return { error: err.message };
  }
};

/**
 * Unpublish an itinerary from the community.
 * @param {string} itineraryId
 * @returns {{ success: boolean, visibility?: string, error?: string }}
 */
export const unpublishFromCommunity = async (itineraryId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/itinerary/${itineraryId}/publish`, {
      method: 'DELETE',
      headers: { ...authHeaders },
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('Error unpublishing from community:', res.status, err);
      return { success: false, error: err.error || 'Failed to unpublish from community' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in unpublishFromCommunity:', err);
    return { success: false, error: err.message };
  }
};

// ============================================================================
// DISPLAY NAME & SEARCH (routed through backend — queries other users)
// ============================================================================

/**
 * Search for wanderers by display name.
 * Routed through backend — no email exposed, returns safe fields only.
 */
export const searchWanderers = async (query, limit = 20) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(
      `${BACKEND_URL}/api/community/search?q=${encodeURIComponent(query)}&limit=${limit}`,
      { headers: { ...authHeaders } }
    );

    if (!res.ok) {
      console.error('Error searching wanderers:', res.status);
      return [];
    }
    return await res.json();
  } catch (err) {
    console.error('Error in searchWanderers:', err);
    return [];
  }
};

// ============================================================================
// TRIP LOGGING (routed through backend)
// ============================================================================

/**
 * Create a new trip log entry.
 * @param {object} params - { title, itineraryId, destinations, startDate, endDate, companions, visibility }
 * @returns {object} Created trip log data or { error }
 */
export const createTripLog = async ({ title, itineraryId, destinations, startDate, endDate, companions, visibility }) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/trip-log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        title,
        itinerary_id: itineraryId,
        destinations,
        start_date: startDate,
        end_date: endDate,
        companions,
        visibility,
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('Error creating trip log:', res.status, err);
      return { error: err.error || 'Failed to create trip log' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in createTripLog:', err);
    return { error: err.message };
  }
};

/**
 * Get trip logs for a specific user.
 * @param {string} userId
 * @param {number} limit
 * @param {number} offset
 * @returns {{ trip_logs: Array, total: number }}
 */
export const getTripLogs = async (userId, limit = 20, offset = 0) => {
  try {
    // Auth headers are required so the backend can detect own-profile requests
    // and return private/followers-only trip logs to the owner. Without them,
    // the backend falls back to visibility='public' only, causing the owner's
    // own trip log page to render empty when their logs aren't public.
    const authHeaders = await getAuthHeaders();
    const res = await fetch(
      `${BACKEND_URL}/api/community/trip-logs/${userId}?limit=${limit}&offset=${offset}`,
      { headers: { ...authHeaders } }
    );

    if (!res.ok) {
      console.error('Error fetching trip logs:', res.status);
      return { trip_logs: [], total: 0 };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in getTripLogs:', err);
    return { trip_logs: [], total: 0 };
  }
};

/**
 * Get a single trip log by ID.
 * @param {string} tripLogId
 * @returns {object|null} Trip log data or null
 */
export const getTripLog = async (tripLogId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/trip-log/${tripLogId}`, {
      headers: { ...authHeaders },
    });

    if (!res.ok) {
      console.error('Error fetching trip log:', res.status);
      return null;
    }
    return await res.json();
  } catch (err) {
    console.error('Error in getTripLog:', err);
    return null;
  }
};

// ============================================================================
// PLACE REVIEWS (routed through backend)
// ============================================================================

/**
 * Submit a review for a place.
 * @param {object} params - { tripLogId, placeName, placeId, city, country, rating, reviewText, tags, visibility }
 * @returns {object} Created review data or { error }
 */
export const submitReview = async ({ tripLogId, placeName, placeId, city, country, rating, reviewText, tags, visibility, sentiment, placeCategory, priceTier, visitDate, companions, photoUrls, userRating }) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        trip_log_id: tripLogId,
        place_name: placeName,
        place_id: placeId,
        city,
        country,
        rating,
        user_rating: userRating,
        sentiment,
        place_category: placeCategory || 'activity',
        price_tier: priceTier || null,
        visit_date: visitDate || null,
        companions: companions || [],
        photo_urls: photoUrls || [],
        review_text: reviewText,
        tags,
        visibility,
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('Error submitting review:', res.status, err);
      return { error: err.error || 'Failed to submit review' };
    }
    invalidateCache('reviews');
    invalidateCache('rankings');
    invalidateCache('feed');
    return await res.json();
  } catch (err) {
    console.error('Error in submitReview:', err);
    return { error: err.message };
  }
};

/**
 * Get reviews for a specific place.
 * @param {string} placeId
 * @param {number} limit
 * @param {number} offset
 * @returns {{ reviews: Array, total: number, average_rating: number }}
 */
export const getPlaceReviews = async (placeId, limit = 20, offset = 0) => {
  try {
    const res = await fetch(
      `${BACKEND_URL}/api/community/reviews/${placeId}?limit=${limit}&offset=${offset}`
    );

    if (!res.ok) {
      console.error('Error fetching place reviews:', res.status);
      return { reviews: [], total: 0, average_rating: 0 };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in getPlaceReviews:', err);
    return { reviews: [], total: 0, average_rating: 0 };
  }
};

/**
 * Get current user's reviews.
 * @param {number} limit
 * @param {number} offset
 * @returns {{ reviews: Array, total: number }}
 */
export const getMyReviews = async (limit = 20, offset = 0) => {
  const cacheKey = `reviews:${limit}:${offset}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(
      `${BACKEND_URL}/api/community/my-reviews?limit=${limit}&offset=${offset}`,
      { headers: { ...authHeaders } }
    );

    if (!res.ok) {
      console.error('Error fetching my reviews:', res.status);
      return { reviews: [], total: 0 };
    }
    const data = await res.json();
    setCache(cacheKey, data);
    return data;
  } catch (err) {
    console.error('Error in getMyReviews:', err);
    return { reviews: [], total: 0 };
  }
};

// ============================================================================
// ACTIVITY FEED (routed through backend — requires auth)
// ============================================================================

/**
 * Get the activity feed for the current user.
 * @param {number} limit
 * @param {number} offset
 * @returns {{ items: Array, total: number }}
 */
export const getFeed = async (limit = 20, offset = 0) => {
  const cacheKey = `feed:${limit}:${offset}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(
      `${BACKEND_URL}/api/community/feed?limit=${limit}&offset=${offset}`,
      { headers: { ...authHeaders } }
    );

    if (!res.ok) {
      console.error('Error fetching feed:', res.status);
      return { items: [], total: 0 };
    }
    const data = await res.json();
    setCache(cacheKey, data, 30_000); // 30s TTL for feed freshness
    return data;
  } catch (err) {
    console.error('Error in getFeed:', err);
    return { items: [], total: 0 };
  }
};

// ============================================================================
// DISCOVERY (routed through backend)
// ============================================================================

/**
 * Get trending destinations.
 * @returns {{ destinations: Array }}
 */
export const getTrending = async () => {
  const cached = getCached('trending');
  if (cached) return cached;
  try {
    const res = await fetch(`${BACKEND_URL}/api/community/trending`);

    if (!res.ok) {
      console.error('Error fetching trending:', res.status);
      return { destinations: [] };
    }
    const data = await res.json();
    setCache('trending', data, 120_000); // 2 min TTL for trending
    return data;
  } catch (err) {
    console.error('Error in getTrending:', err);
    return { destinations: [] };
  }
};

/**
 * Get a single published itinerary with full details for preview.
 * @param {string} itineraryId
 * @returns {object} Full itinerary data with author, or { error }
 */
export const getPublishedItinerary = async (itineraryId) => {
  try {
    const res = await fetch(
      `${BACKEND_URL}/api/community/itinerary/${itineraryId}/view`
    );

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('Error fetching published itinerary:', res.status, err);
      return { error: err.error || 'Itinerary not found' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in getPublishedItinerary:', err);
    return { error: err.message };
  }
};

/**
 * Browse community itineraries with optional filters.
 * @param {object} params - { search, destination, minDays, maxDays, limit, offset }
 * @returns {{ itineraries: Array, total: number }}
 */
export const browseItineraries = async ({ search, destination, tag, minDays, maxDays, limit = 20, offset = 0 } = {}) => {
  try {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    else if (destination) params.append('destination', destination);
    if (tag) params.append('tag', tag);
    if (minDays != null) params.append('min_days', minDays);
    if (maxDays != null) params.append('max_days', maxDays);
    params.append('limit', limit);
    params.append('offset', offset);

    const res = await fetch(
      `${BACKEND_URL}/api/community/itineraries/browse?${params.toString()}`
    );

    if (!res.ok) {
      console.error('Error browsing itineraries:', res.status);
      return { itineraries: [], total: 0 };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in browseItineraries:', err);
    return { itineraries: [], total: 0 };
  }
};

/**
 * Clone a community itinerary into the current user's saved itineraries.
 * @param {string} itineraryId
 * @returns {object} Cloned itinerary data or { error }
 */
export const cloneItinerary = async (itineraryId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/itinerary/${itineraryId}/clone`, {
      method: 'POST',
      headers: { ...authHeaders },
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('Error cloning itinerary:', res.status, err);
      return { error: err.error || 'Failed to clone itinerary' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in cloneItinerary:', err);
    return { error: err.message };
  }
};

// ============================================================================
// BLOCK & REPORT (routed through backend — requires auth)
// ============================================================================

/**
 * Block a user.
 * @param {string} userId - The user to block
 * @returns {{ success: boolean, error?: string }}
 */
export const blockUser = async (userId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/block/${userId}`, {
      method: 'POST',
      headers: { ...authHeaders },
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('Error blocking user:', res.status, err);
      return { success: false, error: err.error || 'Failed to block user' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in blockUser:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Unblock a user.
 * @param {string} userId - The user to unblock
 * @returns {{ success: boolean, error?: string }}
 */
export const unblockUser = async (userId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/block/${userId}`, {
      method: 'DELETE',
      headers: { ...authHeaders },
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('Error unblocking user:', res.status, err);
      return { success: false, error: err.error || 'Failed to unblock user' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in unblockUser:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Get the list of users blocked by the current user.
 * @returns {{ blocked: Array }}
 */
export const getBlockedUsers = async () => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/blocked`, {
      headers: { ...authHeaders },
    });

    if (!res.ok) {
      console.error('Error fetching blocked users:', res.status);
      return { blocked: [] };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in getBlockedUsers:', err);
    return { blocked: [] };
  }
};

/**
 * Report content (user, review, trip log, etc.).
 * @param {object} params - { contentType, contentId, reason, description }
 * @returns {{ success: boolean, error?: string }}
 */
export const reportContent = async ({ contentType, contentId, reason, description }) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        content_type: contentType,
        content_id: contentId,
        reason,
        description,
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('Error reporting content:', res.status, err);
      return { success: false, error: err.error || 'Failed to report content' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in reportContent:', err);
    return { success: false, error: err.message };
  }
};

// ============================================================================
// KUDOS (LIKES/REACTIONS) (routed through backend — requires auth)
// ============================================================================

/**
 * Toggle kudos (like/unlike) on a feed item.
 * @param {string} itemType - 'trip_log' | 'review' | 'published_itinerary'
 * @param {string} itemId - UUID of the item
 * @returns {{ kudos_given: boolean, kudos_count: number } | { error: string }}
 */
export const toggleKudos = async (itemType, itemId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/kudos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ item_type: itemType, item_id: itemId }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('Error toggling kudos:', res.status, err);
      return { error: err.error || 'Failed to toggle kudos' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in toggleKudos:', err);
    return { error: err.message };
  }
};

// ============================================================================
// PRIVACY SETTINGS (routed through backend — requires auth)
// ============================================================================

/**
 * Update the current user's privacy settings.
 * @param {object} settings - Privacy settings object
 * @returns {object|null} Updated settings data or null
 */
export const updatePrivacySettings = async (settings) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/privacy`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(settings),
    });

    if (!res.ok) {
      console.error('Error updating privacy settings:', res.status);
      return null;
    }
    return await res.json();
  } catch (err) {
    console.error('Error in updatePrivacySettings:', err);
    return null;
  }
};

// ============================================================================
// ACHIEVEMENTS & BADGES (Feature #5)
// ============================================================================

/**
 * Get badge definitions catalog
 */
export const getBadgeDefinitions = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/community/badges`);
    if (!res.ok) return [];
    return await res.json();
  } catch (err) {
    console.error('Error in getBadgeDefinitions:', err);
    return [];
  }
};

/**
 * Get a user's earned achievements
 */
export const getUserAchievements = async (userId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/achievements/${userId}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return [];
    return await res.json();
  } catch (err) {
    console.error('Error in getUserAchievements:', err);
    return [];
  }
};

/**
 * Get the current user's city + country unlocks (for re-share grid
 * on the You tab).
 * @returns {{ cities: Array, countries: Array }}
 */
export const getMyUnlocks = async () => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/unlocks`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return { cities: [], countries: [] };
    return await res.json();
  } catch (err) {
    console.error('Error in getMyUnlocks:', err);
    return { cities: [], countries: [] };
  }
};

/**
 * Check and award any newly earned badges for the current user
 */
export const checkAchievements = async () => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/achievements/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
    });
    if (!res.ok) return { newBadges: [] };
    return await res.json();
  } catch (err) {
    console.error('Error in checkAchievements:', err);
    return { newBadges: [] };
  }
};

// ============================================================================
// LEADERBOARDS (Feature #4)
// ============================================================================

/**
 * Get leaderboard data
 * @param {string} type - Leaderboard type: 'global', 'city', 'country'
 * @param {string} location - City or country name (for type='city' or 'country')
 * @param {number} limit - Max results
 */
export const getLeaderboard = async (type = 'global', location = null, limit = 20) => {
  try {
    const params = new URLSearchParams({ type, limit: String(limit) });
    if (location) params.set('location', location);
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/leaderboard?${params}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return { entries: [], userRank: null };
    return await res.json();
  } catch (err) {
    console.error('Error in getLeaderboard:', err);
    return { entries: [], userRank: null };
  }
};

// ============================================================================
// TASTE PROFILE (Feature #9)
// ============================================================================

/**
 * Get a user's taste profile
 */
export const getTasteProfile = async (userId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/taste-profile/${userId}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error('Error in getTasteProfile:', err);
    return null;
  }
};

/**
 * Generate/refresh the current user's taste profile from their review data
 */
export const generateTasteProfile = async () => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/taste-profile/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error('Error in generateTasteProfile:', err);
    return null;
  }
};

// ============================================================================
// PAST PLACES (manual travel history)
// ============================================================================

/**
 * Get user's manually added past places
 */
export const getPastPlaces = async () => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/past-places`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return [];
    return await res.json();
  } catch (err) {
    console.error('Error in getPastPlaces:', err);
    return [];
  }
};

/**
 * Save user's manually added past places
 */
export const savePastPlaces = async (places) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/past-places`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ places }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error('Error in savePastPlaces:', err);
    return null;
  }
};

/**
 * Get taste similarity score between current user and target user
 */
export const getTasteSimilarity = async (targetUserId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/taste-similarity/${targetUserId}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return { similarity: null };
    return await res.json();
  } catch (err) {
    console.error('Error in getTasteSimilarity:', err);
    return { similarity: null };
  }
};

// ============================================================================
// CITY GUIDES (Feature #7)
// ============================================================================

/**
 * Get a city guide by slug
 */
export const getCityGuide = async (slug) => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/community/city-guide/${slug}`);
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error('Error in getCityGuide:', err);
    return null;
  }
};

/**
 * List all available city guides
 */
export const listCityGuides = async (limit = 20) => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/community/city-guides?limit=${limit}`);
    if (!res.ok) return [];
    return await res.json();
  } catch (err) {
    console.error('Error in listCityGuides:', err);
    return [];
  }
};

// ============================================================================
// TRAVEL COMPATIBILITY (Feature #3)
// ============================================================================

/**
 * Get compatibility score with another user
 */
export const getCompatibility = async (otherUserId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/compatibility/${otherUserId}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error('Error in getCompatibility:', err);
    return null;
  }
};

/**
 * Get most compatible users for the current user
 */
export const getMostCompatible = async (limit = 10) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/compatibility/top?limit=${limit}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return [];
    return await res.json();
  } catch (err) {
    console.error('Error in getMostCompatible:', err);
    return [];
  }
};

// ============================================================================
// FIND YOUR PEOPLE — Suggested Follows (Feature #10)
// ============================================================================

/**
 * Get suggested users to follow based on interests/compatibility
 */
export const getSuggestedFollows = async (limit = 10) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/suggested-follows?limit=${limit}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return [];
    return await res.json();
  } catch (err) {
    console.error('Error in getSuggestedFollows:', err);
    return [];
  }
};

/**
 * Get recent follow activity (who followed the user)
 */
export const getFollowActivity = async (userId, limit = 10) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/follow-activity/${userId}?limit=${limit}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.activity || [];
  } catch (err) {
    console.error('Error in getFollowActivity:', err);
    return [];
  }
};

/**
 * Dismiss a suggested follow (won't appear again)
 */
export const dismissSuggestedFollow = async (userId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/suggested-follows/${userId}/dismiss`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
    });
    if (!res.ok) return false;
    return true;
  } catch (err) {
    console.error('Error in dismissSuggestedFollow:', err);
    return false;
  }
};

// ============================================================================
// COMPARATIVE RANKINGS — Beli-style (Feature #2)
// ============================================================================

/**
 * Get next comparison pair for a category
 * @param {string} category — tag-based: 'all', 'must_visit', 'hidden_gem', 'adventure'
 * @param {string|null} city — optional city filter
 * @param {string} itemType — 'destination' | 'activity' | 'restaurant'
 */
export const getNextComparison = async (category, city = null, focalItem = null) => {
  try {
    const authHeaders = await getAuthHeaders();
    const params = new URLSearchParams({ category });
    if (city) params.set('city', city);
    // focalItem: when present, the backend anchors every comparison pair on
    // this place name and picks opponents via binary-search — places the
    // freshly-rated place into the user's ranking in O(log N) comparisons
    // instead of the random pairings the module previously did.
    if (focalItem) params.set('focal_item', focalItem);
    const res = await fetch(`${BACKEND_URL}/api/community/compare/next?${params}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => null);
      return errorData;
    }
    return await res.json();
  } catch (err) {
    console.error('Error in getNextComparison:', err);
    return null;
  }
};

/**
 * Submit a comparison result. winner: 'a' | 'b' | 'skip'
 */
export const submitComparison = async (category, itemA, itemB, winner, city = null) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/compare/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ category, item_a: itemA, item_b: itemB, winner, city }),
    });
    if (!res.ok) return null;
    invalidateCache('rankings');
    return await res.json();
  } catch (err) {
    console.error('Error in submitComparison:', err);
    return null;
  }
};

/**
 * Get user's personal rankings for a category
 * category: 'all' | 'dining' | 'hotel' | 'transport' | 'activity'
 */
export const getPersonalRankings = async (category, city = null, limit = 20) => {
  const cacheKey = `rankings:${category}:${city}:${limit}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;
  try {
    const authHeaders = await getAuthHeaders();
    const params = new URLSearchParams({ category, limit: String(limit) });
    if (city) params.set('city', city);
    const res = await fetch(`${BACKEND_URL}/api/community/compare/rankings?${params}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return [];
    const data = await res.json();
    setCache(cacheKey, data);
    return data;
  } catch (err) {
    console.error('Error in getPersonalRankings:', err);
    return [];
  }
};

// ============================================================================
// BOOKMARKS
// ============================================================================

export const getBookmarks = async (placeCategory = null, city = null) => {
  const cacheKey = `bookmarks:${placeCategory}:${city}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;
  try {
    const authHeaders = await getAuthHeaders();
    const params = new URLSearchParams();
    if (placeCategory) params.set('place_category', placeCategory);
    if (city) params.set('city', city);
    const res = await fetch(`${BACKEND_URL}/api/community/bookmarks?${params}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return [];
    const data = await res.json();
    setCache(cacheKey, data);
    return data;
  } catch (err) {
    console.error('Error in getBookmarks:', err);
    return [];
  }
};

export const createBookmark = async ({ placeName, placeId, city, country, placeCategory, notes }) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/bookmarks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        place_name: placeName,
        place_id: placeId || null,
        city: city || null,
        country: country || null,
        place_category: placeCategory || 'activity',
        notes: notes || null,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { error: err.error || 'Failed to create bookmark' };
    }
    invalidateCache('bookmarks');
    return await res.json();
  } catch (err) {
    console.error('Error in createBookmark:', err);
    return { error: err.message };
  }
};

export const deleteBookmark = async (bookmarkId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/bookmarks/${bookmarkId}`, {
      method: 'DELETE',
      headers: { ...authHeaders },
    });
    if (!res.ok) return { error: 'Failed to delete bookmark' };
    invalidateCache('bookmarks');
    return await res.json();
  } catch (err) {
    console.error('Error in deleteBookmark:', err);
    return { error: err.message };
  }
};

export const checkBookmark = async (placeName, city = null) => {
  try {
    const authHeaders = await getAuthHeaders();
    const params = new URLSearchParams({ place_name: placeName });
    if (city) params.set('city', city);
    const res = await fetch(`${BACKEND_URL}/api/community/bookmarks/check?${params}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return { bookmarked: false };
    return await res.json();
  } catch (err) {
    console.error('Error in checkBookmark:', err);
    return { bookmarked: false };
  }
};

// ============================================================================
// REVIEW PHOTOS
// ============================================================================

export const uploadReviewPhoto = async (base64, filename, contentType = 'image/jpeg') => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/review/upload-photo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ base64, filename, content_type: contentType }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { error: err.error || 'Failed to upload photo' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in uploadReviewPhoto:', err);
    return { error: err.message };
  }
};

// ============================================================================
// CUSTOM TAG REQUESTS
// ============================================================================

export const submitTagRequest = async (tagName, placeCategory = null) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/tag-requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ tag_name: tagName, place_category: placeCategory }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { error: err.error || 'Failed to submit tag request' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in submitTagRequest:', err);
    return { error: err.message };
  }
};

export const getTagRequests = async (status = 'pending') => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/tag-requests?status=${status}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return [];
    return await res.json();
  } catch (err) {
    console.error('Error in getTagRequests:', err);
    return [];
  }
};

export const reviewTagRequest = async (requestId, status) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/tag-requests/${requestId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) return { error: 'Failed to update tag request' };
    return await res.json();
  } catch (err) {
    console.error('Error in reviewTagRequest:', err);
    return { error: err.message };
  }
};

// ============================================================================
// INNER CIRCLE & MESSAGING (Feature #1)
// ============================================================================

/**
 * Send an inner circle invite
 */
export const sendInnerCircleInvite = async (receiverId, message = null) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/inner-circle/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ receiver_id: receiverId, message }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { success: false, error: data.error || 'Failed to send invite' };
    }
    invalidateCache('circle');
    return await res.json();
  } catch (err) {
    console.error('Error in sendInnerCircleInvite:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Respond to an inner circle invite
 */
export const respondToInvite = async (inviteId, accept) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/inner-circle/respond/${inviteId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ accept }),
    });
    if (!res.ok) return { success: false };
    invalidateCache('circle');
    return await res.json();
  } catch (err) {
    console.error('Error in respondToInvite:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Get pending inner circle invites (both sent and received)
 * Returns { received: [], sent: [] }
 */
export const getInnerCircleInvites = async () => {
  const cached = getCached('circle:invites');
  if (cached) return cached;
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/inner-circle/invites`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return { received: [], sent: [] };
    const data = await res.json();
    // Handle both old (array) and new (object) formats
    const result = Array.isArray(data) ? { received: data, sent: [] } : { received: data.received || [], sent: data.sent || [] };
    setCache('circle:invites', result);
    return result;
  } catch (err) {
    console.error('Error in getInnerCircleInvites:', err);
    return { received: [], sent: [] };
  }
};

/**
 * Get inner circle connections
 */
export const getInnerCircle = async () => {
  const cached = getCached('circle:connections');
  if (cached) return cached;
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/inner-circle`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return [];
    const data = await res.json();
    setCache('circle:connections', data);
    return data;
  } catch (err) {
    console.error('Error in getInnerCircle:', err);
    return [];
  }
};

/**
 * Remove an inner circle connection
 */
export const removeFromInnerCircle = async (connectionId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/inner-circle/${connectionId}`, {
      method: 'DELETE',
      headers: { ...authHeaders },
    });
    if (!res.ok) return false;
    invalidateCache('circle');
    return true;
  } catch (err) {
    console.error('Error in removeFromInnerCircle:', err);
    return false;
  }
};

/**
 * Get conversation messages with a user
 */
export const getMessages = async (otherUserId, limit = 50, before = null) => {
  try {
    const authHeaders = await getAuthHeaders();
    const params = new URLSearchParams({ limit: String(limit) });
    if (before) params.set('before', before);
    const res = await fetch(`${BACKEND_URL}/api/community/messages/${otherUserId}?${params}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return [];
    return await res.json();
  } catch (err) {
    console.error('Error in getMessages:', err);
    return [];
  }
};

/**
 * Send a message to an inner circle connection
 */
export const sendMessage = async (receiverId, content) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ receiver_id: receiverId, content }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { error: err.error || 'Failed to send message' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in sendMessage:', err);
    return { error: 'Failed to send message' };
  }
};

/**
 * Get unread message count
 */
export const getUnreadCount = async () => {
  const cached = getCached('circle:unread');
  if (cached !== null) return cached;
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/messages/unread-count`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return 0;
    const data = await res.json();
    const count = data.count || 0;
    setCache('circle:unread', count, 30_000); // 30s for unread count
    return count;
  } catch (err) {
    console.error('Error in getUnreadCount:', err);
    return 0;
  }
};

// ============================================================================
// DESTINATION INSIGHTS — for Planner Integration (Feature #17)
// ============================================================================

/**
 * Get community insights for a destination (used by planner)
 */
export const getDestinationInsights = async (destination, country = null) => {
  try {
    const params = new URLSearchParams({ destination });
    if (country) params.set('country', country);
    const res = await fetch(`${BACKEND_URL}/api/community/destination-insights?${params}`);
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error('Error in getDestinationInsights:', err);
    return null;
  }
};

// ==========================================================================
// ADMIN — Review Queue
// ==========================================================================

// ============================================================================
// PLACE SEARCH — cache_places + community reviews (for QuickRate flow)
// ============================================================================

/**
 * Search places by name, returning merged results from cache_places (Google)
 * and place_reviews (community). Wanderer-reviewed places sort first.
 * @param {string} query - Search text (min 2 chars)
 * @param {string} [city] - Optional city filter
 * @param {number} [limit] - Max results (default 15)
 */
export const searchPlaces = async (query, city, limit = 15) => {
  try {
    const authHeaders = await getAuthHeaders();
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    if (city) params.set('city', city);

    const res = await fetch(`${BACKEND_URL}/api/community/places/search?${params}`, {
      headers: { ...authHeaders },
    });

    if (!res.ok) {
      console.error('Error searching places:', res.status);
      return { places: [], total: 0 };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in searchPlaces:', err);
    return { places: [], total: 0 };
  }
};

// ============================================================================
// ADMIN: UNLINKED REVIEWS (free-text places needing resolution)
// ============================================================================

/**
 * Get reviews that have no place_id (free-text entries needing admin resolution).
 */
export const getAdminUnlinkedReviews = async (limit = 50) => {
  try {
    const authHeaders = await getAuthHeaders();
    const params = new URLSearchParams({ limit: String(limit) });
    const res = await fetch(`${BACKEND_URL}/api/community/admin/unlinked-reviews?${params}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return { reviews: [], total: 0 };
    return await res.json();
  } catch (err) {
    console.error('Error in getAdminUnlinkedReviews:', err);
    return { reviews: [], total: 0 };
  }
};

/**
 * Admin: Link an unlinked review to a place_id.
 */
export const adminLinkReview = async (reviewId, placeId, placeName) => {
  try {
    const authHeaders = await getAuthHeaders();
    const body = { place_id: placeId };
    if (placeName) body.place_name = placeName;
    const res = await fetch(`${BACKEND_URL}/api/community/admin/link-review/${reviewId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(body),
    });
    return await res.json();
  } catch (err) {
    console.error('Error in adminLinkReview:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Admin: Dismiss (delete) a free-text review.
 */
export const adminDismissReview = async (reviewId) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/admin/dismiss-review/${reviewId}`, {
      method: 'DELETE',
      headers: { ...authHeaders },
    });
    return await res.json();
  } catch (err) {
    console.error('Error in adminDismissReview:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Admin: Resolve a free-text place via Google Places API and link the review.
 */
export const adminResolvePlace = async (reviewId, query, city, country) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/admin/resolve-place`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ reviewId, query, city, country }),
    });
    if (!res.ok) {
      const text = await res.text();
      console.error('adminResolvePlace HTTP error:', res.status, text.slice(0, 200));
      return { success: false, error: `Server returned ${res.status}` };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in adminResolvePlace:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Admin: Manually create a place and link a review to it.
 * Used when Google Places API can't find the place.
 */
export const adminCreatePlace = async (reviewId, placeData) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/admin/create-place`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ reviewId, ...placeData }),
    });
    if (!res.ok) {
      const text = await res.text();
      console.error('adminCreatePlace HTTP error:', res.status, text.slice(0, 200));
      return { success: false, error: `Server returned ${res.status}` };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in adminCreatePlace:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Admin: Batch resolve multiple unlinked reviews via Google Places API.
 * @param {string[]} reviewIds
 */
export const adminBatchResolve = async (reviewIds) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/admin/batch-resolve`, {
      method: 'POST',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({ reviewIds }),
    });
    if (!res.ok) {
      const text = await res.text();
      console.error('adminBatchResolve HTTP error:', res.status, text.slice(0, 200));
      return { success: false, error: `Server returned ${res.status}` };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in adminBatchResolve:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Admin: Batch delete multiple unlinked reviews.
 * @param {string[]} reviewIds
 */
export const adminBatchDismiss = async (reviewIds) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/admin/batch-dismiss`, {
      method: 'POST',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({ reviewIds }),
    });
    if (!res.ok) {
      const text = await res.text();
      console.error('adminBatchDismiss HTTP error:', res.status, text.slice(0, 200));
      return { success: false, error: `Server returned ${res.status}` };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in adminBatchDismiss:', err);
    return { success: false, error: err.message };
  }
};

// ============================================================================
// ADMIN: CONTENT REPORTS
// ============================================================================

/**
 * Get content reports for admin review.
 * @param {string} status - 'pending'|'reviewed'|'dismissed'|'all'
 * @param {number} limit
 * @param {number} offset
 */
export const getAdminReports = async (status = 'pending', limit = 50, offset = 0) => {
  try {
    const authHeaders = await getAuthHeaders();
    const params = new URLSearchParams({ status, limit: String(limit), offset: String(offset) });
    const res = await fetch(`${BACKEND_URL}/api/community/admin/reports?${params}`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return { reports: [], total: 0 };
    return await res.json();
  } catch (err) {
    console.error('Error in getAdminReports:', err);
    return { reports: [], total: 0 };
  }
};

/**
 * Get report statistics (pending/reviewed/dismissed counts).
 */
export const getAdminReportStats = async () => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/admin/reports/stats`, {
      headers: { ...authHeaders },
    });
    if (!res.ok) return { pending: 0, reviewed: 0, dismissed: 0, total: 0 };
    return await res.json();
  } catch (err) {
    console.error('Error in getAdminReportStats:', err);
    return { pending: 0, reviewed: 0, dismissed: 0, total: 0 };
  }
};

/**
 * Update a single report (review/dismiss).
 * @param {string} reportId
 * @param {object} params - { action: 'reviewed'|'dismissed', admin_notes?, hide_content? }
 */
export const updateAdminReport = async (reportId, { action, admin_notes, hide_content }) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/admin/reports/${reportId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ action, admin_notes, hide_content }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { success: false, error: err.error };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in updateAdminReport:', err);
    return { success: false, error: 'Failed to update report' };
  }
};

/**
 * Batch update reports (review/dismiss multiple).
 * @param {string[]} reportIds
 * @param {string} action - 'reviewed'|'dismissed'
 */
// ============================================================================
// REVIEW UPDATE + CHECK-INS
// ============================================================================

/**
 * Update an existing review (own reviews only).
 * @param {string} reviewId
 * @param {{ rating?: number, review_text?: string, tags?: string[] }} updates
 */
export const updateReview = async (reviewId, updates) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/review/${reviewId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        rating: updates.rating,
        user_rating: updates.userRating,
        sentiment: updates.sentiment,
        place_category: updates.placeCategory,
        price_tier: updates.priceTier,
        visit_date: updates.visitDate,
        companions: updates.companions,
        photo_urls: updates.photoUrls,
        review_text: updates.reviewText,
        tags: updates.tags,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { error: err.error || 'Failed to update review' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in updateReview:', err);
    return { error: err.message };
  }
};

/**
 * Get a predicted recommendation score for a place.
 * @param {{ placeName: string, city?: string, category?: string }} opts
 * @returns {{ predicted_score, community_avg, community_count, similar_users_score, tag_affinity, confidence }}
 */
export const getRecScore = async ({ placeName, city, category } = {}) => {
  try {
    const authHeaders = await getAuthHeaders();
    const params = new URLSearchParams({ place_name: placeName });
    if (city) params.set('city', city);
    if (category) params.set('category', category);
    const res = await fetch(`${BACKEND_URL}/api/community/rec-score?${params}`, {
      headers: authHeaders,
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error('Error in getRecScore:', err);
    return null;
  }
};

/**
 * Get community average ratings for places.
 * @param {{ placeName?: string, city?: string, category?: string, limit?: number }} opts
 * @returns {Array<{ place_name, city, place_category, avg_rating, review_count, approval_pct }>}
 */
export const getCommunityRatings = async ({ placeName, city, category, limit = 20 } = {}) => {
  try {
    const params = new URLSearchParams();
    if (placeName) params.set('place_name', placeName);
    if (city) params.set('city', city);
    if (category) params.set('category', category);
    params.set('limit', limit);
    const res = await fetch(`${BACKEND_URL}/api/community/community-ratings?${params}`);
    if (!res.ok) return [];
    return await res.json();
  } catch (err) {
    console.error('Error in getCommunityRatings:', err);
    return [];
  }
};

/**
 * Check if the current user has reviewed a specific place.
 * @param {string} placeName
 * @returns {{ review: object|null }}
 */
export const getMyReviewForPlace = async (placeName) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/my-review-for-place?place_name=${encodeURIComponent(placeName)}`, {
      headers: authHeaders,
    });
    if (!res.ok) return { review: null };
    return await res.json();
  } catch (err) {
    console.error('Error in getMyReviewForPlace:', err);
    return { review: null };
  }
};

/**
 * Quick "I've Been Here" check-in for a place.
 * @param {{ placeName, placeId?, city?, country?, tripLogId? }} data
 */
export const checkInPlace = async ({ placeName, placeId, city, country, tripLogId }) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/check-in`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        place_name: placeName,
        place_id: placeId || null,
        city: city || null,
        country: country || null,
        trip_log_id: tripLogId || null,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { error: err.error || 'Failed to check in' };
    }
    return await res.json();
  } catch (err) {
    console.error('Error in checkInPlace:', err);
    return { error: err.message };
  }
};

/**
 * Get all places the current user has checked in to or reviewed.
 * Returns { places: { "place name lowercase": { id, place_name, type, rating } } }
 */
export const getMyCheckins = async () => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/my-checkins`, {
      headers: authHeaders,
    });
    if (!res.ok) return { places: {} };
    return await res.json();
  } catch (err) {
    console.error('Error in getMyCheckins:', err);
    return { places: {} };
  }
};

// ============================================================================
// ADMIN
// ============================================================================

export const batchUpdateReports = async (reportIds, action) => {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/community/admin/reports/batch`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ report_ids: reportIds, action }),
    });
    if (!res.ok) return { success: false };
    return await res.json();
  } catch (err) {
    console.error('Error in batchUpdateReports:', err);
    return { success: false };
  }
};

// ============================================================================
// DESTINATION SCORES (match score + friend scores, Beli-style)
// ============================================================================

/**
 * Get match scores and friend scores for a list of cities.
 * @param {string[]} cities — e.g. ['Paris', 'Tokyo', 'Rome']
 * @returns {{ scores: { [city]: { matchScore, friendScore, friendCount, totalReviews } } }}
 */
export const getDestinationScores = async (cities) => {
  try {
    const authHeaders = await getAuthHeaders();
    if (!authHeaders.Authorization) return { scores: {} };
    const res = await fetch(`${BACKEND_URL}/api/community/destination-scores?cities=${encodeURIComponent(cities.join(','))}`, {
      headers: authHeaders,
    });
    if (!res.ok) return { scores: {} };
    return await res.json();
  } catch (err) {
    console.error('Error in getDestinationScores:', err);
    return { scores: {} };
  }
};

// ============================================================================
// STREAKS (weekly planning streak, Beli-style gamification)
// ============================================================================

/**
 * Get user's current planning streak.
 * @returns {{ currentStreak, longestStreak, totalActiveDays, lastActiveDate, activeThisWeek }}
 */
export const getStreak = async () => {
  try {
    const authHeaders = await getAuthHeaders();
    if (!authHeaders.Authorization) return null;
    const res = await fetch(`${BACKEND_URL}/api/community/streak`, {
      headers: authHeaders,
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error('Error in getStreak:', err);
    return null;
  }
};

// ─── Referral ──────────────────────────────────────────────────
export async function getReferralCode() {
  try {
    const res = await fetch(`${BACKEND_URL}/api/community/referral-code`, { headers: await getAuthHeaders() });
    if (!res.ok) return null;
    const data = await res.json();
    return data.code;
  } catch (e) { console.error('getReferralCode error:', e); return null; }
}

export async function getReferralStats() {
  try {
    const res = await fetch(`${BACKEND_URL}/api/community/referral-stats`, { headers: await getAuthHeaders() });
    if (!res.ok) return { code: null, total_referrals: 0, referral_users: [] };
    return await res.json();
  } catch (e) { console.error('getReferralStats error:', e); return { code: null, total_referrals: 0, referral_users: [] }; }
}

export async function validateReferralCode(code) {
  try {
    const res = await fetch(`${BACKEND_URL}/api/community/referral/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify({ code }),
    });
    if (!res.ok) return { valid: false };
    return await res.json();
  } catch (e) { return { valid: false }; }
}

// ============================================================================
// INSIDER TIPS — user authoring + admin moderation
// ============================================================================

/**
 * List approved insider tips. Optional filters: { city, country, category, limit }.
 */
export const listInsiderTips = async ({ city, country, category, limit = 50 } = {}) => {
  try {
    const params = new URLSearchParams();
    if (city) params.set('city', city);
    if (country) params.set('country', country);
    if (category) params.set('category', category);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    const res = await fetch(
      `${BACKEND_URL}/api/community/insider-tips${qs ? `?${qs}` : ''}`,
      { headers: { ...(await getAuthHeaders()) } }
    );
    if (!res.ok) return { tips: [], total: 0 };
    return await res.json();
  } catch (err) {
    console.error('Error in listInsiderTips:', err);
    return { tips: [], total: 0 };
  }
};

/**
 * Submit a new tip for moderation.
 * @param {{ tip_text, category, city, country?, place_name? }} body
 */
export const submitInsiderTip = async (body) => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/community/insider-tips`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify(body || {}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: data.error || 'Failed to submit tip' };
    return data;
  } catch (err) {
    console.error('Error in submitInsiderTip:', err);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Admin: fetch tips by status. Returns { tips, total, stats:{pending,approved,rejected} }.
 */
export const getAdminInsiderTips = async (status = 'pending', limit = 50, offset = 0) => {
  try {
    const params = new URLSearchParams({ status, limit: String(limit), offset: String(offset) });
    const res = await fetch(
      `${BACKEND_URL}/api/community/admin/insider-tips?${params}`,
      { headers: { ...(await getAuthHeaders()) } }
    );
    if (!res.ok) return { tips: [], total: 0, stats: { pending: 0, approved: 0, rejected: 0 } };
    return await res.json();
  } catch (err) {
    console.error('Error in getAdminInsiderTips:', err);
    return { tips: [], total: 0, stats: { pending: 0, approved: 0, rejected: 0 } };
  }
};

/**
 * Admin: approve a tip.
 */
export const approveInsiderTip = async (tipId) => {
  try {
    const res = await fetch(
      `${BACKEND_URL}/api/community/admin/insider-tips/${tipId}/approve`,
      { method: 'POST', headers: { ...(await getAuthHeaders()) } }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: data.error || 'Failed to approve' };
    return data;
  } catch (err) {
    console.error('Error in approveInsiderTip:', err);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Admin: reject a tip with an optional reason.
 */
export const rejectInsiderTip = async (tipId, reason) => {
  try {
    const res = await fetch(
      `${BACKEND_URL}/api/community/admin/insider-tips/${tipId}/reject`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify({ reason: reason || null }),
      }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: data.error || 'Failed to reject' };
    return data;
  } catch (err) {
    console.error('Error in rejectInsiderTip:', err);
    return { success: false, error: 'Network error' };
  }
};
